import type {
  HealthCheckResponse,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api-console.3pplea.com';

export class UnauthorizedError extends Error {
  constructor() {
    super('Unauthorized');
    this.name = 'UnauthorizedError';
  }
}

export class ForbiddenError extends Error {
  constructor() {
    super('Forbidden');
    this.name = 'ForbiddenError';
  }
}

export class ApiClient {
  private baseUrl: string;
  // In-memory only — never localStorage/sessionStorage/URL. Set after
  // login and after every GET /auth/session bootstrap (which rotates it).
  // The actual session credential is a separate, HttpOnly, __Host-prefixed
  // cookie the browser sends automatically and this code never reads.
  private csrfToken: string | null = null;
  private onUnauthorized: (() => void) | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setCsrfToken(token: string | null): void {
    this.csrfToken = token;
  }

  setUnauthorizedHandler(handler: (() => void) | null): void {
    this.onUnauthorized = handler;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string> | undefined),
      };
      if (this.csrfToken) {
        headers['X-CSRF-Token'] = this.csrfToken;
      }

      const response = await fetch(url, {
        ...options,
        credentials: 'include', // send the HttpOnly session cookie; browser handles it, JS never sees it
        headers,
      });

      if (response.status === 401) {
        this.onUnauthorized?.();
        throw new UnauthorizedError();
      }
      if (response.status === 403) {
        throw new ForbiddenError();
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Auth — deliberately NOT routed through request() above: login/session
  // calls need to read csrfToken out of the response body (not just throw
  // on non-2xx), and must never be gated by requireAdminSession themselves.
  async login(username: string, password: string): Promise<{ success: boolean; role?: string; csrfToken?: string; error?: string }> {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    const data = await response.json();
    if (response.ok && data.csrfToken) {
      this.setCsrfToken(data.csrfToken);
    }
    return data;
  }

  async logout(): Promise<void> {
    await fetch(`${this.baseUrl}/api/v1/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
    this.setCsrfToken(null);
  }

  // Pre-auth, same reasoning as login() above: not routed through request()
  // since there's no session/CSRF token yet, and the response body carries
  // the outcome directly rather than throwing on non-2xx.
  async forgotPassword(username: string): Promise<{ success: boolean; message?: string; error?: string }> {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/forgot-password`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    });
    return response.json();
  }

  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    const response = await fetch(`${this.baseUrl}/api/v1/auth/reset-password`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });
    return response.json();
  }

  // Bootstraps auth state on app load and rotates a fresh CSRF token — the
  // in-memory token above is lost on every page refresh even though the
  // HttpOnly session cookie survives, so this is the mechanism that recovers
  // a usable token after reload.
  async fetchSession(): Promise<{ authenticated: boolean; role?: string; csrfToken?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/auth/session`, {
        credentials: 'include',
      });
      if (!response.ok) {
        return { authenticated: false };
      }
      const data = await response.json();
      if (data.csrfToken) {
        this.setCsrfToken(data.csrfToken);
      }
      return data;
    } catch {
      return { authenticated: false };
    }
  }

  // Health Check
  async healthCheck(): Promise<HealthCheckResponse> {
    return this.request<HealthCheckResponse>('/health');
  }

  // Sanitized monitoring feed (Docker-socket containment migration) — the
  // backend has already validated schema/bounds/staleness; a non-2xx here
  // means "unavailable," not an error to retry-and-hope. feedId selects a
  // secondary droplet's feed (see DropletContext); omitted = home droplet.
  async getStatus(feedId?: string): Promise<{ available: boolean; data?: any; error?: string }> {
    const url = `${this.baseUrl}/api/v1/status${feedId ? `/${encodeURIComponent(feedId)}` : ''}`;
    try {
      const response = await fetch(url, { credentials: 'include' });
      const body = await response.json();
      if (!response.ok) {
        return { available: false, error: body?.error || `HTTP ${response.status}` };
      }
      return { available: true, data: body.data };
    } catch (err) {
      return { available: false, error: err instanceof Error ? err.message : 'Network error' };
    }
  }

  // AI Bot Activity (shared across all 3pplea products)
  async getBotActivity(site?: string, days = 30): Promise<any> {
    const params = new URLSearchParams({ days: String(days) });
    if (site) params.set('site', site);
    const response = await this.request<any>(`/api/v1/bot-activity?${params.toString()}`);
    return response.data || response;
  }
}

export const createApiClient = (baseUrl: string): ApiClient => new ApiClient(baseUrl);

export const api = new ApiClient(API_BASE_URL);
export default api;
